import React from 'react';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { cx, FOCUS_RING } from '../components/styles';

/* ------------------------------------------------------------------ *
 * Content copied verbatim from src/pages/SetupGuidePage.tsx.
 *
 * The only removal is v1's `navigate(-1)` "Back" button: inside a shell with
 * persistent navigation a history-back button is redundant and its destination
 * is unpredictable (DESIGN_PLAN §3.11). Nothing else changes.
 * ------------------------------------------------------------------ */

const EXTENSION_URL =
  'https://chromewebstore.google.com/detail/mmcmdmeolnefbofgffjpjodgfkbaimje?utm_source=item-share-cb';

interface Step {
  number: number;
  tag: string;
  title: string;
  description: string;
  details: string[];
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  action?: { label: string; href: string };
}

const STEPS: Step[] = [
  {
    number: 1,
    tag: 'Install',
    title: 'Download FlashTools Connect',
    description:
      'Install our lightweight Chrome extension. It securely bridges your CrowdVolt session — no passwords stored, tokens auto-expire.',
    details: [
      'Open Google Chrome on your computer',
      'Click the button below to visit the Chrome Web Store',
      'Click "Add to Chrome" and confirm the install',
      'You\'ll see the FlashTools icon appear in your toolbar',
    ],
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/10',
    borderColor: 'border-violet-500/20',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
    action: {
      label: 'Get Chrome Extension',
      href: EXTENSION_URL,
    },
  },
  {
    number: 2,
    tag: 'Connect',
    title: 'Sign into CrowdVolt',
    description:
      'Click "Connect Account" on your FlashTools dashboard. The extension opens CrowdVolt — just log in normally with your phone number and OTP code.',
    details: [
      'On your FlashTools dashboard, tap "Connect CrowdVolt"',
      'A new CrowdVolt tab opens automatically',
      'Enter your phone number and the SMS code you receive',
      'Once logged in, switch back to the FlashTools tab',
    ],
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
        <polyline points="10 17 15 12 10 7" />
        <line x1="15" y1="12" x2="3" y2="12" />
      </svg>
    ),
  },
  {
    number: 3,
    tag: 'Done',
    title: "You're connected",
    description:
      'That\'s it. Your listings, orders, and payouts now sync in real time. Manage everything from one place.',
    details: [
      'Your active listings appear on your dashboard instantly',
      'Edit prices, track sales, and browse events',
      'Connection refreshes automatically — no repeat logins',
    ],
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
];

export const SetupGuideScreen: React.FC = () => {
  return (
    <PageShell>
      <PageHeader
        title="Setup guide"
        subtitle="Follow these steps to link your CrowdVolt account and start managing listings from one dashboard."
      />

      <div className="space-y-6">
        {/* Steps */}
        <div className="space-y-3">
          {STEPS.map((step) => (
            <div
              key={step.number}
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 transition-all hover:border-white/[0.08] hover:bg-white/[0.03]"
            >
              {/* Top row: badge + tag */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${step.bgColor} border ${step.borderColor} flex items-center justify-center flex-shrink-0 ${step.color}`}>
                    {step.icon}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-medium text-white">{step.title}</h2>
                    <span className={`text-[10px] font-medium uppercase tracking-wider ${step.color}`}>
                      Step {step.number} — {step.tag}
                    </span>
                  </div>
                </div>
              </div>

              <p className="mb-4 text-sm leading-relaxed text-white/50">{step.description}</p>

              {/* Checklist */}
              <ul className="mb-4 space-y-2">
                {step.details.map((d, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className={`text-[10px] font-bold w-5 h-5 rounded-md bg-white/[0.04] flex items-center justify-center flex-shrink-0 mt-0.5 ${step.color}`}>
                      {step.number === 3 ? '✦' : j + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-white/60">{d}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {step.action && (
                <a
                  href={step.action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cx(
                    'inline-flex items-center gap-1.5 rounded-lg bg-violet-500 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-violet-500/25 transition-all hover:bg-violet-400',
                    FOCUS_RING,
                  )}
                >
                  {step.action.label}
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17L17 7" />
                    <path d="M7 7h10v10" />
                  </svg>
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Ad Block Warning */}
        <div className="flex items-start gap-4 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 sm:items-center">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-red-500/30 bg-red-500/15">
            <svg className="w-5 h-5 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold uppercase tracking-wide text-red-400">Ad Block Must Be Disabled</p>
            <p className="mt-0.5 text-xs leading-relaxed text-white/50">
              Ad blockers interfere with the extension's ability to connect to CrowdVolt. Please disable any ad blocker before using FlashTools Connect.
            </p>
          </div>
        </div>

        {/* Footer notes */}
        <div className="space-y-3 pt-2">
          <div className="flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/10">
              <svg className="w-5 h-5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="mb-1 text-sm font-medium text-white">Your data stays safe</p>
              <p className="text-xs leading-relaxed text-white/40">
                FlashTools Connect never stores your password. Session tokens are held in memory only and auto-expire after 10 minutes. Your credentials are encrypted at rest with AES-128.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-violet-500/20 bg-violet-500/10">
              <svg className="w-5 h-5 text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="mb-1 text-sm font-medium text-white">Need help?</p>
              <p className="text-xs leading-relaxed text-white/40">
                If the extension isn't capturing your session, make sure you're using Google Chrome and that the FlashTools Connect icon is visible in your toolbar. Try clicking the extension icon to check your connection status.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default SetupGuideScreen;
